import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminHealth } from "@/components/AdminHealth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr, rupiah } from "@/lib/api";
import { PageHeader } from "@/components/Common";

const ROLE = { admin: "Admin", photographer: "Fotografer", member: "Member" };

const Card = ({ label, value, testId, accent }) => (
  <div className="rounded-2xl bg-white border border-zinc-200 p-5">
    <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</p>
    <p className={`font-display text-2xl font-extrabold mt-2 ${accent || ""}`} data-testid={testId}>{value}</p>
  </div>
);

const CommissionSetting = ({ rate, onSaved }) => {
  const [v, setV] = useState(rate);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", { commission_rate: Number(v) });
      toast.success(`Komisi diatur ke ${data.commission_rate}%`);
      onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-6 mb-8 flex flex-col sm:flex-row sm:items-end gap-4">
      <div className="flex-1">
        <p className="font-bold">Komisi platform per transaksi</p>
        <p className="text-sm text-zinc-500">Dipotong dari total pesanan. Berlaku untuk pesanan baru; pesanan lama memakai persentase saat checkout.</p>
      </div>
      <div className="flex items-center gap-2">
        <Input type="number" min={0} max={100} step="0.5" value={v} onChange={(e) => setV(e.target.value)} className="w-28 h-11 rounded-xl" data-testid="commission-rate-input" />
        <span className="text-zinc-500">%</span>
        <Button onClick={save} className="rounded-xl h-11 bg-blue-600 hover:bg-blue-700" data-testid="commission-save-button">Simpan</Button>
      </div>
    </div>
  );
};

const Report = ({ r }) => (
  <>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      <Card label="Transaksi kotor" value={rupiah(r.totals.gross)} testId="report-gross" />
      <Card label="Pendapatan bersih platform" value={rupiah(r.totals.commission)} testId="report-commission" accent="text-emerald-600" />
      <Card label="Hak fotografer" value={rupiah(r.totals.net)} testId="report-net" />
      <Card label="Pesanan lunas" value={`${r.totals.orders} · ${r.totals.photos} foto`} testId="report-orders" />
    </div>
    <h3 className="font-bold mb-3">Per fotografer</h3>
    <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden mb-8">
      <Table data-testid="report-photographer-table">
        <TableHeader><TableRow><TableHead>Fotografer</TableHead><TableHead>Pesanan</TableHead><TableHead>Kotor</TableHead><TableHead>Komisi</TableHead><TableHead>Hak fotografer</TableHead></TableRow></TableHeader>
        <TableBody>
          {r.per_photographer.map((p) => (
            <TableRow key={p.photographer_id}>
              <TableCell className="font-medium">{p.photographer_name}</TableCell>
              <TableCell>{p.orders} ({p.photos} foto)</TableCell>
              <TableCell className="font-mono-x">{rupiah(p.gross)}</TableCell>
              <TableCell className="font-mono-x text-emerald-700">{rupiah(p.commission)}</TableCell>
              <TableCell className="font-mono-x">{rupiah(p.net)}</TableCell>
            </TableRow>
          ))}
          {r.per_photographer.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-10">Belum ada transaksi lunas.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
    <h3 className="font-bold mb-3">Transaksi lunas terbaru</h3>
    <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden">
      <Table data-testid="report-recent-table">
        <TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Member</TableHead><TableHead>Fotografer</TableHead><TableHead>Total</TableHead><TableHead>Komisi</TableHead></TableRow></TableHeader>
        <TableBody>
          {r.recent.map((o) => (
            <TableRow key={o.id}>
              <TableCell className="font-mono-x text-sm">{o.code}</TableCell>
              <TableCell>{o.member.name}</TableCell>
              <TableCell>{o.photographer.name}</TableCell>
              <TableCell className="font-mono-x">{rupiah(o.total)}</TableCell>
              <TableCell className="font-mono-x text-emerald-700">{rupiah(o.commission)} <span className="text-xs text-zinc-400">({o.commission_rate ?? 0}%)</span></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </>
);

const PayoutsTab = () => {
  const [balances, setBalances] = useState([]);
  const [history, setHistory] = useState([]);
  const [target, setTarget] = useState(null);
  const [ref, setRef] = useState("");
  const load = () => {
    api.get("/admin/payouts/balances").then((r) => setBalances(r.data));
    api.get("/admin/payouts").then((r) => setHistory(r.data));
  };
  useEffect(load, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/admin/payouts", { photographer_id: target.photographer_id, reference: ref });
      toast.success(`Pencairan ${rupiah(data.amount)} ke ${data.photographer_name} dicatat`);
      setTarget(null);
      setRef("");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <>
      <h3 className="font-bold mb-3">Saldo fotografer</h3>
      <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden mb-8">
        <Table data-testid="payout-balances-table">
          <TableHeader><TableRow><TableHead>Fotografer</TableHead><TableHead>Rekening</TableHead><TableHead>Pesanan</TableHead><TableHead>Belum dicairkan</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {balances.map((b) => (
              <TableRow key={b.photographer_id} data-testid={`payout-balance-row-${b.photographer_id}`}>
                <TableCell className="font-medium">{b.photographer_name}</TableCell>
                <TableCell className="text-sm">{b.bank_account ? `${b.bank_name} ${b.bank_account} · ${b.bank_holder}` : <span className="text-zinc-400">Belum diisi</span>}</TableCell>
                <TableCell>{b.orders}</TableCell>
                <TableCell className="font-mono-x font-semibold" data-testid={`payout-balance-amount-${b.photographer_id}`}>{rupiah(b.amount)}</TableCell>
                <TableCell className="text-right">
                  {b.amount > 0
                    ? <Button size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700" onClick={() => setTarget(b)} data-testid={`payout-record-${b.photographer_id}`}>Catat pencairan</Button>
                    : <span className="text-xs font-semibold text-emerald-700">Lunas</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <h3 className="font-bold mb-3">Riwayat pencairan</h3>
      <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden">
        <Table data-testid="payout-history-table">
          <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Fotografer</TableHead><TableHead>Pesanan</TableHead><TableHead>Referensi</TableHead><TableHead>Jumlah</TableHead></TableRow></TableHeader>
          <TableBody>
            {history.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{new Date(p.created_at).toLocaleString("id-ID")}</TableCell>
                <TableCell>{p.photographer_name}</TableCell>
                <TableCell className="font-mono-x text-xs">{p.order_codes.join(", ")}</TableCell>
                <TableCell>{p.reference || "-"}</TableCell>
                <TableCell className="font-mono-x text-emerald-700">{rupiah(p.amount)}</TableCell>
              </TableRow>
            ))}
            {history.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-10">Belum ada pencairan.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!target} onOpenChange={(v) => !v && setTarget(null)}>
        <DialogContent className="rounded-2xl" aria-describedby={undefined} data-testid="payout-dialog">
          <DialogHeader><DialogTitle>Catat pencairan dana</DialogTitle></DialogHeader>
          {target && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">Transfer <span className="font-mono-x font-bold text-zinc-900">{rupiah(target.amount)}</span> ke {target.photographer_name} ({target.bank_name} {target.bank_account} a.n. {target.bank_holder}) untuk {target.orders} pesanan, lalu catat di sini.</p>
              <Input placeholder="No. referensi transfer (opsional)" value={ref} onChange={(e) => setRef(e.target.value)} className="h-11 rounded-xl" data-testid="payout-reference-input" />
              <Button onClick={submit} className="w-full rounded-xl h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="payout-submit-button">Tandai sudah dibayar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function AdminPage() {
  const [d, setD] = useState(null);
  const [r, setR] = useState(null);
  const load = () => {
    api.get("/admin/overview").then((res) => setD(res.data));
    api.get("/admin/report").then((res) => setR(res.data));
  };
  useEffect(load, []);
  if (!d || !r) return <p className="p-16 text-zinc-500">Memuat...</p>;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Panel admin" title="Ringkasan Platform" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {[["Komisi saat ini", `${r.commission_rate}%`, "admin-stat-rate"], ["Event", d.events, "admin-stat-events"], ["Foto", d.photos, "admin-stat-photos"], ["Pesanan", d.orders, "admin-stat-orders"]].map(([l, v, t]) => (
          <Card key={t} label={l} value={v} testId={t} />
        ))}
      </div>
      <Tabs defaultValue="report">
        <TabsList className="mb-6 h-auto flex-wrap justify-start">
          <TabsTrigger value="report" data-testid="admin-tab-report">Laporan Pendapatan</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Pencairan Dana</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Pengguna</TabsTrigger>
          <TabsTrigger value="health" data-testid="admin-tab-health">Kesehatan Server</TabsTrigger>
        </TabsList>
        <TabsContent value="health"><AdminHealth /></TabsContent>
        <TabsContent value="payouts"><PayoutsTab /></TabsContent>
        <TabsContent value="report">
          <CommissionSetting rate={r.commission_rate} onSaved={load} />
          <Report r={r} />
        </TabsContent>
        <TabsContent value="users">
      <div className="rounded-2xl bg-white border border-zinc-200 overflow-hidden">
        <Table data-testid="admin-users-table">
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead>Sidik wajah</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-full">{ROLE[u.role]}</Badge></TableCell>
                <TableCell>{u.role === "member" ? (u.has_face ? "Tersimpan" : "-") : ""}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
        </TabsContent>
      </Tabs>
    </main>
  );
}
